import { effect, Injectable, signal } from '@angular/core';
import { MenuItem, SurveyType } from './models/menu-item.model';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { AuthService } from '../../auth/data/auth.service';

const UNDEFINED_MENU_ITEM_ID = -100;

interface AvailableSurvey {
  surveyId: number;
  name: string;
  type: string;
  isAvailable: boolean;
  availableOn: string | null;
  frequency: number;
  hasOnDemandCase?: boolean;
  description: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PillService {
  private selectedMenuItemId = signal<number>(UNDEFINED_MENU_ITEM_ID);
  private menuItemsSignal = signal<MenuItem[]>([]);
  private menuItems$: Observable<AvailableSurvey[]> | null = null;

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService
  ) {
    effect(() => {
      this.loadMenuItems();
    });
  }

  getNextAvailableMenuItem(): MenuItem | null {
    const currentItems = this.menuItemsSignal();
    const selectedItem = currentItems.find(item => item.id !== this.selectedMenuItemId() && item.isAvailable);

    if (!selectedItem) {
      return null;
    }

    return selectedItem;
  }

  getCurrentMenuItem(): MenuItem {
    const currentItems = this.menuItemsSignal();
    const localStorageItemId = localStorage.getItem('selectedMenuItemId');
    const selectedMenuItemId = localStorageItemId ? parseInt(localStorageItemId) : UNDEFINED_MENU_ITEM_ID;
    const selectedItem = currentItems.find(item => item.id === selectedMenuItemId);

    if (!selectedItem) {
      return currentItems[0];
    }

    return selectedItem;
  }

  getSelectedMenuItemId(): number {
    return this.selectedMenuItemId();
  }

  getMenuItems(): MenuItem[] {
    return this.menuItemsSignal();
  }

  setSelectedMenuItemById(id: number): void {
    this.selectedMenuItemId.set(id);
    localStorage.setItem('selectedMenuItemId', id.toString());
  }

  clearMenuItems(): void {
    this.menuItemsSignal.set([]);
    this.menuItems$ = null;
  }

  finishMenuItem(menuItemId: number | null = null): void {
    const currentItems = this.menuItemsSignal();
    let indexToUpdate = currentItems.findIndex(item => item.id === this.selectedMenuItemId());
    let itemToUpdate = this.getCurrentMenuItem();

    if (menuItemId) {
      indexToUpdate = currentItems.findIndex(item => item.id === menuItemId);
      itemToUpdate = currentItems.find(item => item.id === menuItemId) as MenuItem;
    }

    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

    itemToUpdate.progress = 100;
    itemToUpdate.isAvailable = false;
    itemToUpdate.availableOn = twoWeeksFromNow;

    const newItems = [...currentItems];
    newItems[indexToUpdate] = itemToUpdate;

    this.menuItemsSignal.set(newItems);
    this.setSelectedMenuItemById(-1);
  }

  async loadMenuItems(refetch = false): Promise<MenuItem[]> {
    const menuItems = await this.fetchMenuItems(refetch);
    const localStorageItemId = localStorage.getItem('selectedMenuItemId');

    if (!localStorageItemId || localStorageItemId === '-1') {
      this.setSelectedMenuItemById(menuItems[0]?.id ?? -1);
    } else {
      this.setSelectedMenuItemById(parseInt(localStorageItemId));
    }

    return menuItems;
  }

  private async fetchMenuItems(refetch = false): Promise<MenuItem[]> {
    if (!this.authService.isAuthenticated()) {
      return [];
    }

    try {
      if (!this.menuItems$ || refetch) {
        this.menuItems$ = this.httpClient.get<AvailableSurvey[]>('/api/Survey/GetAvailableSurveys', {
          params: {
            includeAnswered: true
          }
        });
      }

      const response = await lastValueFrom(this.menuItems$);

      if (!response) {
        throw new Error('No response from server');
      }

      const menuItems = response
        .map((item, index) => this.mapSurveyToMenuItem(item, index))
        .sort((a, b) => {
          const typeOrder = { [SurveyType.Who5]: 1, [SurveyType.Body]: 2, [SurveyType.Comment]: 3 };
          const aOrder = typeOrder[a.type as keyof typeof typeOrder] || 4;
          const bOrder = typeOrder[b.type as keyof typeof typeOrder] || 4;

          return aOrder - bOrder;
        }) as MenuItem[];
      this.menuItemsSignal.set(menuItems as unknown as MenuItem[]);

      return menuItems;
    } catch (error) {
      console.error('Error fetching menu items:', error);
      this.menuItems$ = null;

      return [];
    }
  }

  private mapSurveyToMenuItem(item: AvailableSurvey, index: number): MenuItem {
    const enpsSurveyName = 'eNPS';
    let icon = 'fa-solid fa-list-check';
    let headLine = item.description ?? 'WebApp.v1.Shared.Pill.Survey.Other.Title';
    let video = item.name === enpsSurveyName ? 'forest_with_car' : index % 2 === 0 ? 'mountain' : 'forest';
    let description = 'WebApp.v1.Shared.Pill.Survey.TimeToFinish';

    switch (item.type) {
      case SurveyType.Who5:
        icon = 'fa-solid fa-brain';
        headLine = 'WebApp.v1.Shared.Pill.Survey.Mind.Title';
        video = 'mind';
        break;
      case SurveyType.Body:
        icon = 'fa-kit fa-survey-body-icon';
        headLine = 'WebApp.v1.Shared.Pill.Survey.Body.Title';
        video = 'man_on_hill';
        description = 'WebApp.v1.Shared.Pill.Survey.TimeToFinish.15s';
        break;
      case SurveyType.Comment:
        icon = 'fa-solid fa-comment';
        headLine = 'WebApp.v1.Shared.Pill.Survey.Comment.Title';
        video = 'lake';
        break;
    }

    return {
      id: item.surveyId,
      title: item.name,
      icon,
      description,
      headLine,
      video,
      progress: item.isAvailable ? 5 : 100,
      isAvailable: item.isAvailable,
      availableOn: item.availableOn ? new Date(item.availableOn) : null,
      type: item.type,
      hasOnDemandCase: item.hasOnDemandCase ?? false,
    };
  }
}